import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Lock, XCircle, Loader2, MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(items.length === 0 ? "empty" : "address");
  const [orderId, setOrderId] = useState(null);
  const [orderResult, setOrderResult] = useState(null);
  const [addr, setAddr] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [pay, setPay] = useState("upi");
  const [paying, setPaying] = useState(false);

  // ── Saved addresses (Profile) ──
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [saveNewAddress, setSaveNewAddress] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/addresses")
      .then(({ data }) => {
        setSavedAddresses(data || []);
        const def = (data || []).find((a) => a.is_default);
        if (def) applySavedAddress(def);
      })
      .catch(() => setSavedAddresses([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
  if (step === "payment") {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}, [step]);

  const applySavedAddress = (a) => {
    setSelectedAddressId(a.id);
    setSaveNewAddress(false);
    setAddr((prev) => ({
      ...prev,
      name: a.name,
      phone: a.phone,
      line1: a.line1,
      line2: a.line2 || "",
      city: a.city,
      state: a.state,
      pincode: a.pincode,
    }));
  };

  const useNewAddress = () => {
    setSelectedAddressId(null);
    setAddr((p) => ({ ...p, name: user?.name || "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" }));
  };

  // ── Live pincode serviceability (Shiprocket) ──
  const [pinCheck, setPinCheck] = useState(null); // { serviceable, cod_available, estimated_delivery_date, courier_name, message }
  const [checkingPin, setCheckingPin] = useState(false);
  const pinDebounce = useRef(null);

  useEffect(() => {
    if (pinDebounce.current) clearTimeout(pinDebounce.current);
    if (!/^\d{6}$/.test(addr.pincode)) {
      setPinCheck(null);
      return;
    }
    pinDebounce.current = setTimeout(async () => {
      setCheckingPin(true);
      try {
        const totalQty = items.reduce((s, i) => s + i.qty, 0) || 1;
        const { data } = await api.post("/orders/serviceability", {
          pincode: addr.pincode,
          qty: totalQty,
        });
        setPinCheck(data);
      } catch (err) {
        setPinCheck({
          serviceable: false,
          message: err?.response?.data?.detail || "Could not verify this pincode right now.",
        });
      } finally {
        setCheckingPin(false);
      }
    }, 450);
    return () => pinDebounce.current && clearTimeout(pinDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addr.pincode]);

  // Auto-drop COD if it's not available for this pincode
  useEffect(() => {
    if (pay === "cod" && pinCheck?.serviceable && pinCheck.cod_available === false) {
      setPay("upi");
      toast.info("Cash on Delivery isn't available for this pincode — switched to UPI.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinCheck]);

  if (items.length === 0 && step !== "success") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="ie-overline text-text-secondary mb-3">Your bag is empty</div>
        <h1 className="font-serif text-4xl mb-8">Nothing to compose yet.</h1>
        <Link to="/shop" className="ie-btn-primary inline-flex">Browse the Atelier</Link>
      </div>
    );
  }

  const submitAddress = (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(addr.pincode)) return toast.error("Enter a valid 6-digit PIN");
    if (!/^\d{10}$/.test(addr.phone.replace(/\D/g, ""))) return toast.error("Enter a valid 10-digit phone");
    if (checkingPin) return toast.error("Please wait for the delivery check to finish.");
    if (!pinCheck || !pinCheck.serviceable) {
      return toast.error(pinCheck?.message || "We don't deliver to this pincode yet.");
    }

    // Optionally persist a freshly-entered address to the user's profile.
    if (user && saveNewAddress && !selectedAddressId) {
      api.post("/addresses", {
        label: "Home",
        name: addr.name,
        phone: addr.phone,
        line1: addr.line1,
        line2: addr.line2,
        city: addr.city,
        state: addr.state,
        pincode: addr.pincode,
        is_default: savedAddresses.length === 0,
      }).then(({ data }) => {
        setSavedAddresses((prev) => [...prev, data]);
        toast.success("Address saved to your profile");
      }).catch(() => {
        // non-fatal — don't block checkout if this fails
      });
    }

    setStep("payment");
  };

const submitPayment = async (e) => {
  e.preventDefault();

  if (pay === "cod") {
    setPaying(true);

    try {
      const payload = {
        items: items.map((i) => ({
           id: i.id,
           name: i.name,
           image: i.image,
           color: i.color,
           size: i.size,
           qty: i.qty,
           price: i.price,
})),
        address: addr,
        payment_method: "cod",
        subtotal,
      };

      const { data } = await api.post("/orders", payload);

      setOrderResult(data);
      setOrderId(data.order_id);

      clear();
      setStep("success");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        "Could not place order. Please try again."
      );
    } finally {
      setPaying(false);
    }

    return;
  }

  setPaying(true);

  try {
    // Load Razorpay Checkout script
    if (!window.Razorpay) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");

        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;

        script.onload = resolve;
        script.onerror = () =>
          reject(new Error("Unable to load Razorpay Checkout."));

        document.body.appendChild(script);
      });
    }

    // Create Razorpay order on our backend
    const { data: paymentOrder } = await api.post(
      "/payments/create-order",
      {
        items: items.map((i) => ({
         id: i.id,
        color: i.color,
         size: i.size,
         qty: i.qty,
      })),
        address: addr,
      }
    );

    const options = {
      key: paymentOrder.key_id,
      amount: paymentOrder.amount,
      currency: paymentOrder.currency,
      name: "INDUS EIRENE",
      description: "Premium clothing",
      order_id: paymentOrder.razorpay_order_id,

      prefill: {
        name: addr.name,
        email: addr.email,
        contact: addr.phone,
      },

      notes: {
        payment_reference: paymentOrder.payment_reference,
      },

      theme: {
        color: "#111111",
      },

      handler: async function (response) {
        try {
          const { data: verification } = await api.post(
            "/payments/verify",
            {
              razorpay_order_id:
                response.razorpay_order_id,

              razorpay_payment_id:
                response.razorpay_payment_id,

              razorpay_signature:
                response.razorpay_signature,
            }
          );

          if (!verification.success) {
            throw new Error("Payment verification failed.");
          }

          toast.success("Payment verified successfully.");

          setOrderResult(null);
          setOrderId(verification.order_id);

          clear();
          setStep("success");

        } catch (err) {
          toast.error(
            err?.response?.data?.detail ||
            "Payment verification failed. Please contact support."
          );
        } finally {
          setPaying(false);
        }
      },

      modal: {
        ondismiss: function () {
          setPaying(false);
          toast.info("Payment window closed.");
        },
      },
    };

    const razorpay = new window.Razorpay(options);

    razorpay.on("payment.failed", function (response) {
      console.error(
        "Razorpay payment failed:",
        response.error
      );

      setPaying(false);

      toast.error(
        response?.error?.description ||
        "Payment failed. Please try again."
      );
    });

    razorpay.open();

  } catch (err) {
    console.error("Razorpay checkout error:", err);

    toast.error(
      err?.response?.data?.detail ||
      err?.message ||
      "Unable to start payment. Please try again."
    );

    setPaying(false);
  }
};


  const STEPS = ["Bag", "Address", "Payment", "Confirmation"];
  const currentIdx = step === "address" ? 1 : step === "payment" ? 2 : step === "success" ? 3 : 0;
  const codBlocked = pinCheck?.serviceable && pinCheck.cod_available === false;

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="checkout-page">
      {/* Steps */}
      <div className="flex items-center justify-center gap-3 md:gap-6 mb-12 flex-wrap" data-testid="checkout-steps">
        {STEPS.map((label, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-7 h-7 flex items-center justify-center border ${i <= currentIdx ? "bg-foreground text-background border-foreground" : "border-border text-text-secondary"} ie-overline`}>
              {i + 1}
            </div>
            <span className={`ie-overline ${i === currentIdx ? "text-foreground" : "text-text-secondary"}`}>{label}</span>
            {i < STEPS.length - 1 && <span className="hidden md:inline w-12 h-px bg-border"/>}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === "address" && (
          <motion.div key="address" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <h1 className="font-serif text-4xl tracking-tight mb-8">Delivery Address</h1>

              {user && savedAddresses.length > 0 && (
                <div className="mb-8" data-testid="saved-addresses-picker">
                  <div className="ie-overline text-text-secondary mb-3">Use a saved address</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {savedAddresses.map((a) => (
                      <button
                        type="button"
                        key={a.id}
                        onClick={() => applySavedAddress(a)}
                        className={`text-left border p-4 text-sm transition-colors ${
                          selectedAddressId === a.id ? "border-brand-accent bg-muted" : "border-border hover:border-foreground"
                        }`}
                        data-testid={`saved-address-${a.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="ie-overline text-brand-accent inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {a.label}
                          </span>
                          {a.is_default && <span className="ie-overline text-text-secondary">Default</span>}
                          {selectedAddressId === a.id && <Check className="w-3.5 h-3.5 text-brand-accent" />}
                        </div>
                        <div className="font-serif">{a.name}</div>
                        <div className="text-text-secondary text-xs mt-1 leading-relaxed">
                          {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} – {a.pincode}
                        </div>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={useNewAddress}
                    className="ie-overline text-text-secondary hover:text-foreground mt-3"
                    data-testid="use-new-address"
                  >
                    + Use a different address
                  </button>
                </div>
              )}

              <form onSubmit={submitAddress} className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6" data-testid="address-form">
                <Field label="Full name" value={addr.name} onChange={(v) => setAddr({...addr, name: v})} testid="address-name" colSpan="sm:col-span-2"/>
                <Field label="Email" type="email" value={addr.email} onChange={(v) => setAddr({...addr, email: v})} testid="address-email"/>
                <Field label="Phone" value={addr.phone} onChange={(v) => setAddr({...addr, phone: v.replace(/\D/g, "").slice(0, 10)})} testid="address-phone"/>
                <Field label="Address line 1" value={addr.line1} onChange={(v) => setAddr({...addr, line1: v})} testid="address-line1" colSpan="sm:col-span-2"/>
                <Field label="Address line 2 (optional)" value={addr.line2} onChange={(v) => setAddr({...addr, line2: v})} required={false} testid="address-line2" colSpan="sm:col-span-2"/>
                <Field label="City" value={addr.city} onChange={(v) => setAddr({...addr, city: v})} testid="address-city"/>
                <Field label="State" value={addr.state} onChange={(v) => setAddr({...addr, state: v})} testid="address-state"/>

                <div>
                  <label className="ie-overline text-text-secondary block mb-2">PIN code</label>
                  <input
                    value={addr.pincode}
                    onChange={(e) => setAddr({ ...addr, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    required
                    className="ie-input"
                    data-testid="address-pincode"
                  />
                  {checkingPin && (
                    <div className="text-xs text-text-secondary mt-2 flex items-center gap-1" data-testid="pincode-checking">
                      <Loader2 className="w-3 h-3 animate-spin" /> Checking delivery…
                    </div>
                  )}
                  {!checkingPin && pinCheck && (
                    pinCheck.serviceable ? (
                      <div className="text-xs text-brand-accent mt-2 flex items-center gap-1" data-testid="pincode-result-ok">
                        <CheckCircle2 className="w-3 h-3" />
                        Delivers here
                        {pinCheck.estimated_delivery_date
                          ? ` · by ${new Date(pinCheck.estimated_delivery_date).toLocaleDateString("en-IN", { month: "short", day: "numeric" })}`
                          : ""}
                      </div>
                    ) : (
                      <div className="text-xs text-red-500 mt-2 flex items-center gap-1" data-testid="pincode-result-bad">
                        <XCircle className="w-3 h-3" />
                        {pinCheck.message || "Not deliverable to this pincode"}
                      </div>
                    )
                  )}
                </div>

                {user && !selectedAddressId && (
                  <label className="sm:col-span-2 flex items-center gap-3 text-sm text-text-secondary cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveNewAddress}
                      onChange={(e) => setSaveNewAddress(e.target.checked)}
                      className="w-4 h-4 accent-current"
                      data-testid="checkout-save-address-checkbox"
                    />
                    Save this address to my profile
                  </label>
                )}

                <div className="sm:col-span-2 flex justify-end mt-4">
                  <button className="ie-btn-primary w-full sm:w-auto justify-center" data-testid="address-continue">Continue to Payment</button>
                </div>
              </form>
            </div>
            <Summary items={items} subtotal={subtotal} pinCheck={pinCheck}/>
          </motion.div>
        )}

        {step === "payment" && (
          <motion.div key="payment" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-7 space-y-8">
              <div>
                <button onClick={() => setStep("address")} className="ie-overline text-text-secondary hover:text-foreground" data-testid="back-to-address">← Edit address</button>
                <h1 className="font-serif text-4xl tracking-tight mt-2">Payment</h1>
                <div className="ie-overline text-brand-accent mt-2 inline-flex items-center gap-2"><Lock className="w-3 h-3"/> Secure Razorpay checkout</div>
              </div>

              <form onSubmit={submitPayment} className="space-y-6" data-testid="payment-form">
                <div className="border border-border" data-testid="payment-options">
                  {[
                    ["upi", "UPI · Google Pay / PhonePe"],
                    ["card", "Credit / Debit Card"],
                    ...(codBlocked ? [] : [["cod", "Cash on Delivery"]]),
                  ].map(([id, label]) => (
                    <label key={id} className={`flex items-center gap-4 p-5 border-b last:border-b-0 border-border cursor-pointer ${pay === id ? "bg-muted" : ""}`} data-testid={`payment-option-${id}`}>
                      <input type="radio" name="pay" value={id} checked={pay === id} onChange={() => setPay(id)} className="accent-brand-accent"/>
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
                {codBlocked && (
                  <p className="text-xs text-text-secondary" data-testid="cod-unavailable-note">
                    Cash on Delivery isn't available for this address — please pay via UPI or card.
                  </p>
                )}

               <button
  disabled={paying}
  className="ie-btn-primary w-full justify-center"
  data-testid="pay-now"
>
  {paying
    ? "Processing…"
    : pay === "cod"
      ? `Place Order · ₹${subtotal.toLocaleString("en-IN")}`
      : `Pay ₹${subtotal.toLocaleString("en-IN")}`}
</button>
            <p className="text-[11px] text-text-secondary text-center"> Payments are securely processed by Razorpay.</p>
            </form>
            </div>
            <Summary items={items} subtotal={subtotal} pinCheck={pinCheck}/>
          </motion.div>
        )}

        {step === "success" && (
          <motion.div key="success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16 max-w-2xl mx-auto" data-testid="checkout-success">
            <CheckCircle2 className="w-12 h-12 text-brand-accent mx-auto mb-6"/>
            <div className="ie-overline text-brand-accent mb-3">Quietly placed</div>
            <h1 className="font-serif text-5xl tracking-tight mb-4">Thank you, {addr.name?.split(" ")[0] || "friend"}.</h1>
            {/* <p className="text-text-secondary text-sm mb-2 leading-relaxed">
              Order <span className="font-serif text-foreground">{orderId}</span> has been confirmed. A confirmation has been sent to {addr.email}.
            </p> */}
            <p className="text-text-secondary text-sm mb-2 leading-relaxed">
                 Order <span className="font-serif text-foreground">{orderId}</span> has been placed.
                 You will receive an automated call shortly to confirm your order.
            </p>
            {orderResult?.estimated_delivery && (
              <p className="text-text-secondary text-sm mb-2">
                Estimated delivery by{" "}
                <span className="font-serif text-foreground">
                  {new Date(orderResult.estimated_delivery).toLocaleDateString("en-IN", { weekday: "long", month: "long", day: "numeric" })}
                </span>
              </p>
            )}
            {orderResult?.courier_name && (
              <p className="text-text-secondary text-xs mb-8">Shipped via {orderResult.courier_name}</p>
            )}
            <div className="flex justify-center gap-3 flex-wrap mt-6">
              <Link to={`/track?id=${orderId}&email=${encodeURIComponent(addr.email)}`} className="ie-btn-ghost" data-testid="success-track-order">Track Order</Link>
              <button onClick={() => navigate("/")} className="ie-btn-primary" data-testid="success-continue">Continue Shopping</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, colSpan = "", required = true }) {
  return (
    <div className={colSpan}>
      <label className="ie-overline text-text-secondary block mb-2">{label}</label>
      <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="ie-input" data-testid={testid}/>
    </div>
  );
}

function Summary({ items, subtotal, pinCheck }) {
  return (
    <aside className="md:col-span-5">
      <div className="bg-surface border border-border p-8 sticky top-28" data-testid="checkout-summary">
        <div className="ie-overline mb-6">Order · {items.length} {items.length === 1 ? "edition" : "editions"}</div>
        <ul className="space-y-4 max-h-72 overflow-y-auto">
          {items.map((i) => (
            <li key={i.sku} className="flex gap-3">
              <div className="w-14 h-16 bg-background overflow-hidden flex-shrink-0">
                <img src={i.image} alt="" className="w-full h-full object-cover"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-sm truncate">{i.name}</div>
                <div className="ie-overline text-text-secondary mt-0.5">Size {i.size} · Qty {i.qty}</div>
              </div>
              <div className="font-serif text-sm">₹{(i.price * i.qty).toLocaleString("en-IN")}</div>
            </li>
          ))}
        </ul>
        <div className="border-t border-border mt-6 pt-6 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-text-secondary">Subtotal</span><span>₹{subtotal.toLocaleString("en-IN")}</span></div>
          <div className="flex justify-between"><span className="text-text-secondary">Shipping</span><span className="text-text-secondary">Free</span></div>
          {pinCheck?.serviceable && pinCheck.estimated_delivery_date && (
            <div className="flex justify-between" data-testid="summary-eta">
              <span className="text-text-secondary">Est. delivery</span>
              <span>{new Date(pinCheck.estimated_delivery_date).toLocaleDateString("en-IN", { month: "short", day: "numeric" })}</span>
            </div>
          )}
          <div className="border-t border-border pt-3 flex justify-between items-baseline">
            <span className="ie-overline">Total</span>
            <span className="font-serif text-2xl">₹{subtotal.toLocaleString("en-IN")}</span>
          </div>
        </div>
      </div>
    </aside>
  );
}